import { toast } from 'sonner';

import { Button } from '@/components/ui/button';

import { isLlmProfileError } from '../errors';
import { useSetDefaultLlmProfile } from '../hooks/useSetDefaultLlmProfile';
import type { LlmProfile } from '../types';

/**
 * Offers a "Set as default" action on a model's non-default profiles (spec
 * FR-015a, acceptance scenario 7). Renders nothing for the default profile
 * itself, or when there is only one profile in the group — with exactly one,
 * that profile is implicitly default and gets no interactive control at all
 * (acceptance scenario 8): there is nothing else to promote away from, and
 * offering a control would invite a pointless click. No "Default" badge is
 * shown in either case; the list carries no default indicator today.
 */
export function SetDefaultProfileControl({
  profile,
  siblingCount,
}: {
  profile: LlmProfile;
  siblingCount: number;
}) {
  const setDefault = useSetDefaultLlmProfile();

  if (siblingCount <= 1 || profile.is_default) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={setDefault.isPending}
      onClick={() => {
        setDefault.mutate(profile.slug, {
          onError: (error) => {
            toast.error(
              isLlmProfileError(error)
                ? error.message
                : 'Could not set this profile as default. Please try again.',
            );
          },
        });
      }}
    >
      Set as default
    </Button>
  );
}
